import { DeleteOutlined, EditOutlined } from "@ant-design/icons"
import { Flex } from "antd"
import type { MenuProps, TablePaginationConfig } from "antd"
import type { ColumnsType } from "antd/es/table"
import type {
  FilterValue,
  SortOrder,
  SorterResult
} from "antd/es/table/interface"
import React, { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { CardComponent } from "../../../components/Card/CardComponent"
import { EntityTable } from "../../../components/EntityTable/EntityTable.tsx"
import { RowActionsMenu } from "../../../components/EntityTable/RowActionsMenu.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../components/EntityTable/surfaces.ts"
import { SearchBar } from "../../../components/Input/SearchBar"
import { TableResultCount } from "../../../components/Table/TableResultCount.tsx"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useScrolledTable } from "../../../hooks/use-scrolled-table.ts"
import { cn } from "../../../utils/cn.ts"
import { getTableOrderingFromSorter } from "../../../utils/table"
import type { SupplierListItem } from "../types/types.ts"
import { SuppliersEmpty } from "./SuppliersEmpty.tsx"

interface SuppliersListProps {
  suppliers: SupplierListItem[]
  // Forwarded to EntityTable so a filter change drops the row selection.
  selectionFilters?: unknown
  search: string | null
  ordering: string | null
  loading: boolean
  next?: string | null
  retryNext?: string | null
  count: number
  canEdit: boolean
  canDelete: boolean
  onLoadMore: () => void
  onSearchChange: (search: string | null) => void
  onOrderingChange: (ordering: string | null) => void
  onRowAction: (record: SupplierListItem, action: "edit" | "delete") => void
}

export const SuppliersList = ({
  suppliers,
  selectionFilters,
  search,
  ordering,
  loading,
  next,
  retryNext,
  count,
  canEdit,
  canDelete,
  onLoadMore,
  onSearchChange,
  onOrderingChange,
  onRowAction
}: SuppliersListProps) => {
  const { t } = useTranslation()

  const { tableRef, tableHeight, onTableScroll } = useScrolledTable({
    loading: loading,
    next: next,
    loadMore: onLoadMore,
    fullPage: true
  })

  const onChangeTable = useCallback(
    (
      _pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<SupplierListItem> | SorterResult<SupplierListItem>[]
    ) => {
      onOrderingChange(getTableOrderingFromSorter(sorter))
    },
    [onOrderingChange]
  )

  // The slice holding `ordering` outlives this component, so the sort has to be
  // driven from it rather than from AntD's own header state: a remount would
  // otherwise render no arrow, and the next paginate would hand the empty
  // sorter back as "no sort" and silently drop the ordering.
  const sortOrderFor = useCallback(
    (key: string): SortOrder => {
      if (!ordering) return null
      const descending = ordering.startsWith("-")
      if ((descending ? ordering.slice(1) : ordering) !== key) return null
      return descending ? "descend" : "ascend"
    },
    [ordering]
  )

  const columns = useMemo<ColumnsType<SupplierListItem>>(
    () => [
      {
        title: t("suppliers.columns.name"),
        dataIndex: "name",
        key: "name",
        sorter: true,
        sortOrder: sortOrderFor("name"),
        align: "left",
        render: (value: string, record: SupplierListItem) => (
          <Link to={RoutesPaths.suppliers.view(record.id)}>{value}</Link>
        )
      },
      {
        title: t("suppliers.columns.email"),
        dataIndex: "email",
        key: "email",
        sorter: true,
        sortOrder: sortOrderFor("email"),
        render: (value: string | null) => value || "—"
      },
      {
        title: t("suppliers.columns.phone"),
        dataIndex: "phone",
        key: "phone",
        sorter: true,
        sortOrder: sortOrderFor("phone"),
        render: (value: string | null) => value || "—"
      },
      {
        title: t("suppliers.columns.address"),
        dataIndex: "address",
        key: "address",
        sorter: true,
        sortOrder: sortOrderFor("address"),
        render: (value: string | null) => value || "—"
      },
      // Dropped entirely when the user may neither edit nor delete, so a
      // view-only user gets no empty column and no 403 to click into.
      ...(canEdit || canDelete
        ? [
            {
              title: t("suppliers.columns.actions"),
              width: 96,
              align: "center" as const,
              render: (_value: unknown, record: SupplierListItem) => {
                const items: MenuProps["items"] = [
                  ...(canEdit
                    ? [
                        {
                          key: "edit",
                          label: t("suppliers.actions.edit"),
                          icon: <EditOutlined />,
                          onClick: () => onRowAction(record, "edit")
                        }
                      ]
                    : []),
                  ...(canDelete
                    ? [
                        {
                          key: "delete",
                          label: t("suppliers.actions.delete"),
                          icon: <DeleteOutlined />,
                          danger: true,
                          onClick: () => onRowAction(record, "delete")
                        }
                      ]
                    : [])
                ]
                // This list only qualified for `RowActionsMenu` once the card
                // shell gave it a `scroll.y`: its body scrolls internally now,
                // so a menu left on `document.body` would hang over whichever
                // row scrolled under it.
                return (
                  <RowActionsMenu
                    items={items}
                    placement="bottomRight"
                    buttonProps={{ type: "text", size: "small" }}
                  />
                )
              }
            }
          ]
        : [])
    ],
    [canEdit, canDelete, onRowAction, sortOrderFor, t]
  )

  return (
    <CardComponent classNames={{ body: "p-0" }}>
      <Flex
        wrap="wrap"
        gap="small"
        className="py-4 md:p-4 border-b border-gray-200"
      >
        <div className="flex-1 max-md:basis-60 max-md:grow">
          <SearchBar
            placeholder={t("suppliers.searchPlaceholder")}
            defaultValue={search ?? ""}
            onSearch={onSearchChange}
          />
        </div>
      </Flex>
      <div ref={tableRef}>
        <EntityTable<SupplierListItem>
          surface={TABLE_SURFACES.suppliers}
          selectionMode="on-demand"
          selectionFilters={selectionFilters}
          tab={DEFAULT_TAB}
          rowKey="id"
          columns={columns}
          dataSource={suppliers}
          loading={loading && !suppliers?.length}
          scroll={{
            x: "max-content",
            y: tableHeight,
            scrollToFirstRowOnChange: true
          }}
          onChange={onChangeTable}
          onScroll={onTableScroll}
          withOutPagination
          pagination={false}
          rowHoverable={true}
          locale={{ emptyText: <SuppliersEmpty hasSearch={!!search} /> }}
          className={cn({
            "[&_.ant-table-cell-scrollbar]:w-0 [&_.ant-table-cell-scrollbar]:p-1":
              !suppliers?.length
          })}
        />
        <TableResultCount
          loaded={suppliers?.length ?? 0}
          total={count}
          loading={loading}
          next={next}
          retryNext={retryNext}
          onRetry={onLoadMore}
        />
      </div>
    </CardComponent>
  )
}
